<script setup lang="ts">
import type { HeaderProps } from '~/utils/header'
import { ContentDiv, ContentGrid } from '#components'

const props = defineProps<HeaderProps>()

const meta = useHeaderMeta(() => props)

const openSearch = ref(false)

const openMenu = ref(false)

const headerRef = useTemplateRef('header')

useDrawerAutoClose(openMenu, {
  desktopBreakpoint: `(min-width: ${NAV_COLLAPSE_MIN_WIDTH[getNavigationContext()]}px)`,
  closeOnRouteChange: false,
  focusOnAutoClose: headerRef,
})

const { bodyAttrs, translateY, hasScrolled } = useHeaderState(headerRef, openMenu, openSearch)

const transform = computed(() => meta.value.scrollable && typeof translateY.value === 'string' ? `translateY(${translateY.value})` : 'none')

const { theme } = useTheme()

useHead({
  bodyAttrs,
})

const route = useRoute()

watch(() => route.name, () => {
  openSearch.value = false
  openMenu.value = false
})

const showSearch = computed(() => meta.value.context === 'default' && route.name !== 'search-index' && route.name !== 'oversikt-index')
</script>

<template>
  <header ref="header" tabindex="-1" :class="[headerVariants.base({ theme, scrolled: meta.scrollable && hasScrolled }), $attrs.class]">
    <component :is="meta.narrow ? ContentGrid : ContentDiv">
      <HeaderDesktopMenu
        v-model:open="openMenu"
        v-model:open-search="openSearch"
        :show-search="showSearch"
      />

      <HeaderMobileMenu
        v-model:open="openMenu"
      />
    </component>

    <Transition v-if="showSearch" name="slide-down">
      <HeaderSearch v-model:open="openSearch" />
    </Transition>
  </header>

  <Transition v-if="showSearch" name="backdrop-fade">
    <div v-if="openSearch" class="fixed inset-0 bg-carbon-300/50 z-20" aria-hidden="true" />
  </Transition>
</template>

<style scoped lang="postcss">
header {
  transform: v-bind(transform);
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 500ms ease-out;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

.backdrop-fade-enter-to,
.backdrop-fade-leave-from {
  opacity: 1;
}

.slide-down-enter-active {
  transition:
    opacity 200ms ease-out,
    transform 500ms ease-out;
}

.slide-down-leave-active {
  transition:
    opacity 500ms ease-out,
    transform 500ms ease-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-1.5rem);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
